"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MAX_GUEST_CAPACITY } from "@/lib/guest-capacity";

export interface Guests {
  adults: number;
  children: number;
  infants: number;
}

// Fallback cap when no per-unit `max` is passed (i.e. the catalogue-wide search
// selectors) — the largest party any unit can host. Per-unit booking selectors
// still pass the exact unit capacity via `max`.
const DEFAULT_MAX = MAX_GUEST_CAPACITY;

interface Props {
  value: Guests;
  onChange: (g: Guests) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Maximum adults+children. Defaults to 8 for pre-search pickers; the
   *  BookingCard passes the unit's actual capacity so a 6-sleeper home can't
   *  be booked for 8 people. */
  max?: number;
  /** Collapse to a single "Guests" stepper (no children/infants split). The
   *  inquiry only stores the headcount (adults+children), so the breakdown was
   *  UI-only — this just simplifies it per operator. */
  adultsOnly?: boolean;
  locale?: "en" | "ar";
}

export default function GuestSelector({ value, onChange, onClose, anchorRef, max, adultsOnly, locale = "en" }: Props) {
  const MAX = max && max > 0 ? max : DEFAULT_MAX;
  const L = locale === "ar"
    ? { guests: "الضيوف", adults: "البالغون", adultsSub: "13 سنة فأكثر", children: "الأطفال", childrenSub: "من 2 إلى 12 سنة", infants: "الرضّع", infantsSub: "أقل من سنتين · لا يُحتسبون ضمن الحد" }
    : { guests: "Guests", adults: "Adults", adultsSub: "Ages 13+", children: "Children", childrenSub: "Ages 2-12", infants: "Infants", infantsSub: "Under 2 · don't count toward limit" };
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Phones get a bottom sheet + backdrop instead of an anchored popover —
  // the anchored version had no backdrop, so on mobile the guest form had no
  // obvious way to dismiss. Matches DatePicker's md (768px) breakpoint.
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [isMobile]);

  // Popover is position: fixed → coordinates are viewport-relative. Desktop only.
  useEffect(() => {
    if (isMobile || !anchorRef.current) return;
    const place = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const width = 360;
      setPos({
        top: r.bottom + 8,
        left: Math.max(16, Math.min(r.left, window.innerWidth - width - 16)),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
    };
  }, [anchorRef, isMobile]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  function bump(key: keyof Guests, delta: number) {
    const next = { ...value, [key]: value[key] + delta };
    if (next.adults < 1) return;
    if (next.children < 0 || next.infants < 0) return;
    if (key !== "infants" && next.adults + next.children > MAX) return;
    if (key === "infants" && next.infants > 5) return;
    onChange(next);
  }

  if (typeof document === "undefined") return null;
  if (!isMobile && !pos) return null;
  const sheetMode = isMobile;

  const sum = value.adults + value.children;

  return createPortal(
    <>
    {sheetMode && (
      <div
        className="fixed inset-0 z-[219] bg-black/40 backdrop-blur-sm fade-up"
        onClick={onClose}
        aria-hidden="true"
      />
    )}
    <div
      ref={popRef}
      className={
        sheetMode
          ? "fixed inset-x-0 bottom-0 bg-white border-t border-ss-line rounded-t-[22px] shadow-[var(--shadow-ss-xl)] z-[220] p-5 pb-[max(20px,env(safe-area-inset-bottom))] fade-up"
          : "fixed bg-white border border-ss-line rounded-[14px] shadow-[var(--shadow-ss-xl)] z-[220] p-4 min-w-[340px] max-w-[calc(100vw-32px)] fade-up"
      }
      style={sheetMode ? undefined : { top: pos!.top, left: pos!.left }}
    >
      {adultsOnly ? (
        <Row
          title={L.guests}
          sub=""
          value={value.adults}
          canDec={value.adults > 1}
          canInc={value.adults < MAX}
          onDec={() => bump("adults", -1)}
          onInc={() => bump("adults", 1)}
          last
        />
      ) : (
        <>
          <Row
            title={L.adults}
            sub={L.adultsSub}
            value={value.adults}
            canDec={value.adults > 1}
            canInc={sum < MAX}
            onDec={() => bump("adults", -1)}
            onInc={() => bump("adults", 1)}
          />
          <Row
            title={L.children}
            sub={L.childrenSub}
            value={value.children}
            canDec={value.children > 0}
            canInc={sum < MAX}
            onDec={() => bump("children", -1)}
            onInc={() => bump("children", 1)}
          />
          <Row
            title={L.infants}
            sub={L.infantsSub}
            value={value.infants}
            canDec={value.infants > 0}
            canInc={value.infants < 5}
            onDec={() => bump("infants", -1)}
            onInc={() => bump("infants", 1)}
            last
          />
        </>
      )}
      <div className="text-[12.5px] text-ss-muted mt-3.5">
        {adultsOnly ? `${MAX} guest maximum.` : `${MAX} guest maximum · infants don't count.`}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="w-full bg-ss-blue text-white font-semibold py-2.5 rounded-full mt-3 hover:bg-ss-blue-dark"
      >
        Apply
      </button>
    </div>
    </>,
    document.body
  );
}

function Row({
  title,
  sub,
  value,
  canDec,
  canInc,
  onDec,
  onInc,
  last,
}: {
  title: string;
  sub: string;
  value: number;
  canDec: boolean;
  canInc: boolean;
  onDec: () => void;
  onInc: () => void;
  last?: boolean;
}) {
  return (
    <div
      className={`flex justify-between items-center py-3 ${
        last ? "" : "border-b border-ss-line"
      }`}
    >
      <div>
        <div className="font-semibold text-sm">{title}</div>
        {sub && <div className="text-[12.5px] text-ss-muted">{sub}</div>}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onDec}
          disabled={!canDec}
          className="w-8 h-8 rounded-full border border-ss-line grid place-items-center text-base disabled:opacity-30 disabled:cursor-not-allowed hover:border-ss-text"
          aria-label={`Remove ${title.toLowerCase()}`}
        >
          −
        </button>
        <span className="w-6 text-center font-semibold text-sm">{value}</span>
        <button
          type="button"
          onClick={onInc}
          disabled={!canInc}
          className="w-8 h-8 rounded-full border border-ss-line grid place-items-center text-base disabled:opacity-30 disabled:cursor-not-allowed hover:border-ss-text"
          aria-label={`Add ${title.toLowerCase()}`}
        >
          +
        </button>
      </div>
    </div>
  );
}
